/**
 * Après un enregistrement refusé : ce qui manque sur cette partie, puis le
 * chemin le plus court.
 *
 * Il remplace le bandeau rouge « Une étape est incomplète. Nous vous y avons
 * ramené. », qui restait affiché sur toutes les étapes et ne nommait aucun
 * champ (retour d'Éclosion, 24/10/2026). Il ne paraît que sur une partie en
 * cause lors du refus, et sa liste suit la fiche : un champ rempli en sort
 * aussitôt. La partie complète, il propose d'aller droit à la suivante qui
 * manque, ou au récapitulatif — sans repasser toutes les étapes, ce qui sur
 * téléphone voulait dire cinq « Continuer » d'affilée.
 */

import { AlertCircle, ArrowRight, CheckCircle2 } from 'lucide-react';
import { ETAPES, type Erreurs, type Etape } from './fiche';

const BandeauDesManques = ({
  manques,
  prochaine,
  onAller,
}: {
  /** Ce qui manque encore sur cette partie. */
  manques: Erreurs;
  /** La prochaine partie incomplète, ou `null` si la fiche est complète. */
  prochaine: Etape | null;
  onAller: (e: Etape) => void;
}) => {
  const champs = Object.entries(manques);

  if (champs.length > 0) {
    return (
      <div className="mb-6 rounded-xl bg-amber-50 p-4 ring-1 ring-amber-200" role="status">
        <p className="flex items-center gap-2 text-sm font-semibold text-amber-900">
          <AlertCircle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          {champs.length === 1
            ? 'Il reste une chose à compléter ici :'
            : `Il reste ${champs.length} choses à compléter ici :`}
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-6 text-sm text-amber-900">
          {champs.map(([champ, message]) => (
            <li key={champ}>{message}</li>
          ))}
        </ul>
      </div>
    );
  }

  /* Le libellé court, celui de l'indicateur d'étapes : le titre entier passait sur deux lignes sur téléphone. */
  const nom = prochaine ? ETAPES.find((e) => e.cle === prochaine)?.court : null;
  return (
    <div
      className="mb-6 flex flex-col gap-3 rounded-xl bg-green-50 p-4 ring-1 ring-green-200 sm:flex-row sm:items-center sm:justify-between"
      role="status"
    >
      <p className="flex items-center gap-2 text-sm font-medium text-green-900">
        <CheckCircle2 className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        {prochaine ? 'Cette partie est complète.' : 'Tout est complet.'}
      </p>
      <button
        type="button"
        onClick={() => onAller(prochaine ?? 'recapitulatif')}
        className="btn-primary inline-flex min-h-[44px] items-center justify-center gap-2 px-4 text-sm"
      >
        {prochaine ? `Compléter « ${nom} »` : 'Revenir au récapitulatif'}
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
};

export default BandeauDesManques;
